import { cn } from '@invent/wl-ui-kit-next';

import { HelpPopover } from '@/design';
import { formatDateTime } from '@/domains/survey';
import type { IOversightResponseRow } from '../../api/oversight-contracts';
import type { IAdminParticipantDetail } from '../../api/participant-contracts';
import { absent } from '../../components/account-facts';
import { Fact } from '../../components/admin-page';

/**
 * A participant's record dates — REC-06's four timestamps, each from a member survey-api computed
 * (the advisor record page's F11, and F12's Q2, ruled (a)). Drawn in the hub's header for the
 * participant opened and on each member's Overview card for that member.
 *
 * - **In the platform since** — the card's `createdAt`, the participant row's creation. For a
 *   migrated participant that is the load's clock, not Salesforce's; Salesforce's own "Date
 *   Created" is a staff field, under Veritant research. The header says both, for every participant
 *   — on a record page behind "About these dates" (SR-406), on the household's page under the value
 *   (the F11 walk's M1): nothing on the card says a participant was migrated, and guessing it from
 *   the date would be deriving what survey-api does not send.
 * - **Submitted** — of the member's response submitted last (the newest `submittedAt`), its first
 *   submission (`firstSubmittedAt`, survey-api's earliest snapshot) and its last, with its survey
 *   and cycle; one date where the two are the same, or where survey-api sent no first.
 * - **Advisor's last edit** — the newest `lastRespondentWriteAt` among the member's responses, with
 *   its survey and cycle: survey-api's latest event made by an advisor account, a team's accounts
 *   included — so "advisor's" for a team too, since who on a team wrote is not served — and never
 *   moved by a staff correction. Null on every one is "None recorded in the platform": a migrated
 *   response records none, which the header says once. Read without the member (an API before
 *   it), the fact is not drawn: absent is not null.
 * - **Staff's last edit** — `lastInternalEdit`, drawn as sent (R9): what it counts is survey-api's,
 *   and nothing here takes a newest over the staff fields', figures' or placements' own stamps,
 *   which was B6's whole point. Null is "No staff edit in the platform yet", never "never edited":
 *   the migration's values are Salesforce's edits, which survey-api leaves out. A card without the
 *   member (a survey-api before block 10) draws no such fact.
 *
 * Nothing here computes a date but the newest of the member's own rows' members. Rejected: the
 * response's `updatedAt` as an edit date ("Answers last changed", F11's), which a staff correction
 * moves too — retired once survey-api served the respondent's own.
 */
export function RecordDates({
  participant,
  responses,
  header = false,
  compact = false
}: {
  participant: IAdminParticipantDetail;
  /** The hub's responses — the household's whole list; the member's are picked out here. */
  responses: IOversightResponseRow[];
  /** Drawn in the header: four across a wide screen, and what a date is for a migrated
   *  participant said under "In the platform since" and "Advisor's last edit" — once on the page.
   *  An Overview card is half as wide, two across. */
  header?: boolean;
  /** A record page's card (SR-406): a strip across the card's whole width, four across, label over
   *  value — Lightning's highlights panel — and the two sentences about migrated records behind one
   *  "About these dates" press instead of under the values. */
  compact?: boolean;
}) {
  const explain = header && !compact;
  const own = responses.filter(
    (row) => row.participant.participantId === participant.participantId
  );
  const submitted = newest(own, (row) => row.submittedAt);
  const written = newest(own, (row) => row.lastRespondentWriteAt ?? null);
  // Drawn only where survey-api sent the member, or where there is no response to carry it.
  const writesServed =
    own.length === 0 || own.some((row) => row.lastRespondentWriteAt !== undefined);
  const edit = participant.lastInternalEdit;

  const dates = (
    <div
      role="group"
      aria-label="Dates"
      className={cn(
        'grid gap-3 sm:grid-cols-2',
        explain && 'xl:grid-cols-4',
        compact && 'lg:grid-cols-4'
      )}
    >
      <Fact
        label="In the platform since"
        value={
          <>
            {formatDateTime(participant.createdAt)}
            {explain && (
              <span className="block text-xs text-muted-foreground">{MIGRATED_CREATED}</span>
            )}
          </>
        }
      />
      <Fact
        label="Submitted"
        value={
          submitted ? (
            <>
              {submissions(submitted.row.firstSubmittedAt, submitted.at)}
              <Which row={submitted.row} compact={compact} />
            </>
          ) : (
            absent(
              own.length === 0
                ? compact
                  ? '—'
                  : 'No response held.'
                : compact
                  ? 'Not yet'
                  : 'Nothing submitted yet.'
            )
          )
        }
      />
      {writesServed && (
        <Fact
          label="Advisor’s last edit"
          value={
            <>
              {written ? (
                <>
                  {formatDateTime(written.at)}
                  <Which row={written.row} compact={compact} />
                </>
              ) : (
                absent(
                  own.length === 0
                    ? compact
                      ? '—'
                      : 'No response held.'
                    : compact
                      ? 'None recorded'
                      : 'None recorded in the platform.'
                )
              )}
              {explain && (
                <span className="block text-xs text-muted-foreground">{MIGRATED_EDIT}</span>
              )}
            </>
          }
        />
      )}
      {edit !== undefined && (
        <Fact
          label="Staff’s last edit"
          value={
            edit ? (
              <>
                {formatDateTime(edit.at)}
                <span className="block text-muted-foreground">
                  {edit.by ? `by ${edit.by.email}` : 'by an account not recorded'}
                </span>
              </>
            ) : (
              absent(compact ? 'None yet' : `No staff edit in the platform yet — ${STAFF_EDITS}`)
            )
          }
        />
      )}
    </div>
  );
  if (!compact) return dates;
  return (
    <div className="grid gap-2">
      <div className="flex items-center gap-1">
        <span className="text-xs tracking-wide text-muted-foreground uppercase">Dates</span>
        <HelpPopover label="About these dates" className="text-inherit">
          <p>{MIGRATED_CREATED}</p>
          <p>{MIGRATED_EDIT}</p>
          <p>Staff’s last edit counts edits made in the platform — {STAFF_EDITS}</p>
        </HelpPopover>
      </div>
      {dates}
    </div>
  );
}

/** What "In the platform since" is for a migrated record — said once on the page. */
const MIGRATED_CREATED =
  'For a migrated participant “In the platform since” is the day of the migration; Salesforce’s ' +
  'Date Created is under Veritant research → Information.';

/** What Staff's last edit leaves out. */
const STAFF_EDITS = 'Salesforce’s edits are not counted.';

/** The response a date is of — its survey and cycle; on the record card its survey alone, small
 *  (SR-407): the cycle is the summary's, a line below. */
function Which({ row, compact }: { row: IOversightResponseRow; compact: boolean }) {
  return (
    <span className={cn('block text-muted-foreground', compact && 'text-xs')}>
      {compact ? row.survey.name : responseOf(row)}
    </span>
  );
}

/** Why a migrated response shows no advisor edit. */
const MIGRATED_EDIT = 'A response migrated from Salesforce records no advisor edit.';

/** A response's submissions: "first …, last …", or the one instant where survey-api's two are the
 *  same — one submission — or no first was sent. */
function submissions(first: Nullable<string> | undefined, last: string): string {
  if (!first || first === last) return formatDateTime(last);
  return `first ${formatDateTime(first)}, last ${formatDateTime(last)}`;
}

/** The row whose instant is the latest, and the instant — none where no row carries one. An
 *  instant `Date.parse` cannot read is passed over, so it can neither win nor block a later one. */
function newest(
  rows: IOversightResponseRow[],
  instant: (row: IOversightResponseRow) => Nullable<string>
): Nullable<{ row: IOversightResponseRow; at: string }> {
  let found: Nullable<{ row: IOversightResponseRow; at: string }> = null;
  for (const row of rows) {
    const at = instant(row);
    if (!at || Number.isNaN(Date.parse(at))) continue;
    if (found === null || Date.parse(at) > Date.parse(found.at)) found = { row, at };
  }
  return found;
}

function responseOf(row: IOversightResponseRow): string {
  return `${row.survey.name}, ${row.cycle.name}`;
}
